import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import type { Contact, Group } from '../../ContactsAndGroups/ContactsAndGroupsContext'
import type { WeeklyGeneralReport, WeeklyTrapCatchesReport, WeeklyTrapIssuesReport } from '../_types'
import {
  WEEKLY_TRAP_CATCHES_REPORT_TYPE,
  WEEKLY_TRAP_ISSUES_REPORT_TYPE,
  type TReportType,
} from '../ReportForm/ReportType/ReportType'
import { defaultDates } from './defaultDates'
import type { TPestSelected, TReportStartEnd } from './formTypesAndStyles'

export const convertAPIReportToInitialValues = ({
  initialValues,
  reportType,
}: {
  reportType: TReportType
  initialValues: WeeklyGeneralReport | WeeklyTrapCatchesReport | WeeklyTrapIssuesReport | null
}) => {
  const propertiesObject = fieldAssetStore.getState().properties
  const reportId = (initialValues && initialValues.id) || null

  let reportProperties: TProperty[] = []

  if (propertiesObject) {
    reportProperties = Object.values(propertiesObject)
  }

  const hasSouthernHemisphereProperties = Object.values(reportProperties ?? {}).some(
    (p) => p.isSouthernHemisphere,
  )

  const hasNorthernHemisphereProperties = Object.values(reportProperties ?? {}).some(
    (p) => !p.isSouthernHemisphere,
  )

  if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE || reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE) {
    reportProperties = reportProperties.filter((p) =>
      p.valuesTimeseries.some((v) => v.startsWith('insectTrapCatches')),
    )
  }

  const defaultDatesToUse =
    hasSouthernHemisphereProperties && !hasNorthernHemisphereProperties
      ? defaultDates.southernHemisphere
      : defaultDates.northernHemisphere

  const contacts: Contact[] =
    (initialValues && initialValues.contacts && initialValues.contacts.length && initialValues.contacts) || []

  const groups: Group[] =
    (initialValues &&
      initialValues.groups &&
      initialValues.groups.length &&
      initialValues.groups.map((g) => {
        let groupContacts: typeof g.contacts = []

        g.contacts.forEach((c) => {
          const contact = groupContacts.find((contact) => contact.id === c.id)

          if (!contact) {
            groupContacts.push(c)
          }
        })

        return { ...g, contacts: groupContacts }
      })) ||
    []

  const dayOfWeek = (initialValues && initialValues?.rules?.dayOfWeek) || null
  const name = (initialValues && initialValues.name) || null
  const notifyOwnerByEmail = initialValues ? initialValues.notifyOwnerByEmail : true // should be true by default
  const initialRules = initialValues?.rules
  const pestRules = initialValues?.rules?.pests
  const pests: TPestSelected = {}

  let pestType = translate.phrases.banyanApp('Include all available pests with traps')

  if (
    pestRules?.length &&
    (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE || reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE)
  ) {
    pestType = translate.phrases.banyanApp('Include the following selected pests')
  }

  let coverageType = 'all'

  if (initialRules && initialRules.properties && initialRules.properties.length > 0) {
    coverageType = 'properties'
  }

  if (pestRules?.length) {
    pestRules.forEach((pest) => {
      if ('insect' in pest) {
        pests[+pest.insect] = {
          insectId: pest.insect,
          degreeDays: false,
          trapCatches: true,
          endDate: pest.endDate ?? null,
          startDate: pest.startDate ?? null,
        }
      } else {
        pests[+pest.id] = {
          insectId: pest.id,
          degreeDays: pest.degreeDays,
          trapCatches: pest.trapCatches,
          endDate: pest.endDate ?? null,
          startDate: pest.startDate ?? null,
        }
      }
    })
  }

  let trapCatchStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.pestStartDate,
    endDate: defaultDatesToUse.pestEndDate,
  }

  if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE && initialRules && 'startDate' in initialRules) {
    trapCatchStartEnd = {
      startDate: initialRules.startDate ?? defaultDatesToUse.pestStartDate,
      endDate: initialRules.endDate ?? defaultDatesToUse.pestEndDate,
    }
  }

  let trapIssuesStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.trapIssuesStartDate,
    endDate: defaultDatesToUse.trapIssuesEndDate,
  }

  if (reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE && initialRules && 'startDate' in initialRules) {
    trapIssuesStartEnd = {
      startDate: initialRules.startDate ?? defaultDatesToUse.trapIssuesStartDate,
      endDate: initialRules.endDate ?? defaultDatesToUse.trapIssuesEndDate,
    }
  }

  let weather = false

  let weatherStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.weatherStartDate,
    endDate: defaultDatesToUse.weatherEndDate,
  }

  if (initialRules && 'weather' in initialRules && initialRules?.weather) {
    weather = true

    weatherStartEnd = {
      startDate: initialRules.weather.startDate,
      endDate: initialRules.weather.endDate,
    }
  }

  let fireBlight = false

  let fireBlightStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.fireBlightStartDate,
    endDate: defaultDatesToUse.fireBlightEndDate,
  }

  if (initialRules && 'fireBlight' in initialRules && initialRules.fireBlight) {
    fireBlight = true

    fireBlightStartEnd = {
      startDate: initialRules.fireBlight?.startDate,
      endDate: initialRules.fireBlight?.endDate,
    }
  }

  let evapotranspiration = false

  let evapotranspirationStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.evapotranspirationStartDate,
    endDate: defaultDatesToUse.evapotranspirationEndDate,
  }

  if (initialRules && 'evapotranspiration' in initialRules && initialRules.evapotranspiration) {
    evapotranspiration = true

    evapotranspirationStartEnd = {
      startDate: initialRules.evapotranspiration.startDate,
      endDate: initialRules.evapotranspiration.endDate,
    }
  }

  let irrigationActivity = false

  let irrigationActivityStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.irrigationActivityStartDate,
    endDate: defaultDatesToUse.irrigationActivityEndDate,
  }

  if (initialRules && 'irrigationActivity' in initialRules && initialRules.irrigationActivity) {
    irrigationActivity = true

    irrigationActivityStartEnd = {
      startDate: initialRules.irrigationActivity.startDate,
      endDate: initialRules.irrigationActivity.endDate,
    }
  }

  let soilMoisture = false

  let soilMoistureStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.soilMoistureStartDate,
    endDate: defaultDatesToUse.soilMoistureEndDate,
  }

  if (initialRules && 'soilMoisture' in initialRules && initialRules.soilMoisture) {
    soilMoisture = true

    soilMoistureStartEnd = {
      startDate: initialRules.soilMoisture.startDate,
      endDate: initialRules.soilMoisture.endDate,
    }
  }

  let chill = false

  let chillStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.chillStartDate,
    endDate: defaultDatesToUse.chillEndDate,
  }

  if (initialRules && 'chill' in initialRules && initialRules.chill) {
    chill = true

    chillStartEnd = {
      startDate: initialRules.chill.startDate,
      endDate: initialRules.chill.endDate,
    }
  }

  let pollination = false

  let pollinationStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.pollinationStartDate,
    endDate: defaultDatesToUse.pollinationEndDate,
  }

  if (initialRules && 'pollination' in initialRules && initialRules.pollination) {
    pollination = true

    pollinationStartEnd = {
      startDate: initialRules.pollination.startDate,
      endDate: initialRules.pollination.endDate,
    }
  }

  let growingDegreeDays = false

  let growingDegreeDaysStartEnd: TReportStartEnd = {
    startDate: defaultDatesToUse.growingDegreeDaysStartDate,
    endDate: defaultDatesToUse.growingDegreeDaysEndDate,
  }

  if (initialRules && 'growingDegreeDays' in initialRules && initialRules.growingDegreeDays) {
    growingDegreeDays = true

    growingDegreeDaysStartEnd = {
      startDate: initialRules.growingDegreeDays.startDate,
      endDate: initialRules.growingDegreeDays.endDate,
    }
  }

  let selectedProperties: TOptions[] = []

  if (initialRules?.properties && propertiesObject) {
    const targetIds = initialValues?.rules?.properties

    selectedProperties = Object.values(propertiesObject)
      .filter((property) => {
        // Check if the property's id is included in the targetIds array
        return targetIds?.some((targetId: { id: string }) => Number(targetId.id) === property.propertyId)
      })
      .map((property) => {
        return {
          label: property.propertyName,
          value: property.propertyId.toString(),
          id: `${property.propertyId}-PROPERTY`,
        }
      })
  }

  return {
    reportId,
    reportType,
    selectedProperties,
    contacts,
    reportProperties,
    groups,
    name,
    notifyOwnerByEmail,
    dayOfWeek,
    weather,
    weatherStartEnd,
    evapotranspiration,
    evapotranspirationStartEnd,
    irrigationActivity,
    irrigationActivityStartEnd,
    soilMoisture,
    soilMoistureStartEnd,
    chill,
    chillStartEnd,
    pollination,
    pollinationStartEnd,
    growingDegreeDays,
    growingDegreeDaysStartEnd,
    pests,
    pestType,
    coverageType,
    trapCatchStartEnd,
    trapIssuesStartEnd,
    fireBlight,
    fireBlightStartEnd,
  }
}
